import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, MessageCircle } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import MentalHealthScore from '../components/MentalHealthScore';
import CareerOperatingPanel from '../components/domains/CareerOperatingPanel';
import RelationshipWeather from '../components/domains/RelationshipWeather';
import WealthBarometer from '../components/domains/WealthBarometer';
import SpiritualityCompass from '../components/domains/SpiritualityCompass';
import FamilyHarmony from '../components/domains/FamilyHarmony';
import EducationPath from '../components/domains/EducationPath';
import TravelNavigator from '../components/domains/TravelNavigator';
import { TaraCard, TaraCardContent } from '../components/ui/TaraCard';
import AskSageAnything from '../components/AskSageAnything';
import { getAgent } from '../agents/agentPersonalities';

const DomainDetailUniversal = () => {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const [showAgentChat, setShowAgentChat] = useState(false);
  const agent = getAgent(domainId);

  const domainConfig = {
    health: {
      name: 'Health & Longevity',
      icon: '❤️',
      gradient: 'from-red-100 to-pink-100',
      component: MentalHealthScore,
    },
    career: {
      name: 'Career & Success',
      icon: '💼',
      gradient: 'from-amber-100 to-orange-100',
      component: CareerOperatingPanel,
    },
    relationships: {
      name: 'Relationships',
      icon: '💑',
      gradient: 'from-pink-100 to-rose-100',
      component: RelationshipWeather,
    },
    wealth: {
      name: 'Wealth & Finance',
      icon: '💰',
      gradient: 'from-green-100 to-emerald-100',
      component: WealthBarometer,
    },
    family: {
      name: 'Family & Children',
      icon: '👨‍👩‍👧‍👦',
      gradient: 'from-cyan-100 to-blue-100',
      component: FamilyHarmony,
    },
    education: {
      name: 'Education & Skills',
      icon: '📚',
      gradient: 'from-indigo-100 to-purple-100',
      component: EducationPath,
    },
    spirituality: {
      name: 'Spirituality',
      icon: '🕉️',
      gradient: 'from-purple-100 to-fuchsia-100',
      component: SpiritualityCompass,
    },
    travel: {
      name: 'Travel & Settlement',
      icon: '✈️',
      gradient: 'from-teal-100 to-cyan-100',
      component: TravelNavigator,
    },
  };

  const domain = domainConfig[domainId];
  const DomainComponent = domain?.component;

  if (!domain) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-stone-900 mb-2">Domain not found</h2>
          <button onClick={() => navigate('/my-domains')} className="text-cosmic-600">
            Back to Domains
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      {/* Header with gradient */}
      <div className={`bg-gradient-to-br ${domain.gradient} px-5 py-8`}>
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate('/my-domains')}
            className="mb-4 p-2 hover:bg-white/50 rounded-full transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-stone-700" />
          </button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="text-6xl">{domain.icon}</div>
            <div>
              <h1 className="font-serif text-3xl font-medium text-stone-900">
                {domain.name}
              </h1>
              <p className="text-sm text-stone-600 mt-1">
                Your personalized intelligence panel
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 py-6 max-w-2xl mx-auto space-y-6">
        {/* Ask Agent Button */}
        {agent && (
          <button
            onClick={() => setShowAgentChat(true)}
            className="w-full bg-gradient-to-r from-cosmic-500 to-sacred-500 hover:from-cosmic-600 hover:to-sacred-600 text-white py-4 px-6 rounded-button font-medium transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
          >
            <MessageCircle className="w-5 h-5" />
            <span>Ask {agent.name} Anything</span>
            <span className="text-xl">{agent.avatar}</span>
          </button>
        )}

        {/* Domain Content */}
        {DomainComponent ? (
          <DomainComponent />
        ) : (
          <TaraCard className="text-center p-8">
            <TaraCardContent>
              <div className="text-5xl mb-4">{domain.icon}</div>
              <h3 className="font-serif text-xl font-medium text-stone-900 mb-2">
                {domain.name}
              </h3>
              <p className="text-sm text-stone-600 mb-4">
                Coming soon with advanced intelligence panels
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cosmic-100 text-cosmic-700 rounded-full text-sm font-medium">
                ✨ Under Development
              </div>
            </TaraCardContent>
          </TaraCard>
        )}
      </div>

      {/* AI Agent Chat Modal */}
      {showAgentChat && (
        <AskSageAnything 
          domainId={domainId}
          onClose={() => setShowAgentChat(false)}
        />
      )}

      <BottomNav active="domains" />
    </div>
  );
};

export default DomainDetailUniversal;
